import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { AppDispatch, useAppDispatch } from 'redux/store';
import { fetchNodeInstanceFile } from '../../../../redux/slices/node_instancesSlice';

const NodeInstanceFileViewer: React.FC = () => {
  const { nodeInstanceId, fileName } = useParams<{
    nodeInstanceId: string;
    fileName: string;
  }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [fileContent, setFileContent] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fileType, setFileType] = useState<string>('');

  useEffect(() => {
    const loadFile = async () => {
      if (!nodeInstanceId || !fileName) {
        setError('Missing node instance ID or file name');
        setIsLoading(false);
        return;
      }

      try {
        const result = await dispatch(
          fetchNodeInstanceFile({
            node_instance_id: parseInt(nodeInstanceId),
            file_name: fileName,
          })
        ).unwrap();

        setFileContent(result);
        setError(null);

        // Determine file type from extension
        const extension = fileName.split('.').pop()?.toLowerCase() || '';
        setFileType(extension);
      } catch (err: any) {
        setError(err.error || 'Failed to load file');
        setFileContent(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadFile();
  }, [dispatch, nodeInstanceId, fileName]);

  const handleClose = () => {
    window.close();
  };

  const renderFileContent = () => {
    if (!fileContent) return null;

    // Handle different file types
    switch (fileType) {
      case 'json':
        return (
          <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {JSON.stringify(JSON.parse(fileContent), null, 2)}
          </pre>
        );
      case 'html':
        return <div dangerouslySetInnerHTML={{ __html: fileContent }} />;
      case 'txt':
      case 'log':
      case 'md':
        return (
          <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {fileContent}
          </pre>
        );
      case 'csv':
        return (
          <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {fileContent}
          </pre>
        );
      default:
        // For unknown file types, try to display as text
        return (
          <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {fileContent}
          </pre>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card m-3">
        <div className="card-body">
          <div className="text-danger mb-3">{error}</div>
          <button className="btn btn-primary" onClick={handleClose}>
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card m-3">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">File: {fileName}</h4>
          <button className="btn btn-primary" onClick={handleClose}>
            Close
          </button>
        </div>
        <div className="border rounded p-3 bg-light">{renderFileContent()}</div>
      </div>
    </div>
  );
};

export default NodeInstanceFileViewer;
